import apiClient from './apiClient'

export interface AbsenceRequest {
  id: number
  employeeId: number
  employeeFullName: string
  absenceTypeId: number
  absenceTypeName: string
  startDate: string
  endDate: string
  totalDays: number
  totalHours: number
  location: string | null
  status: AbsenceRequestStatus
  note: string | null
  managerNote: string | null
  requestedAt: string
  decidedAt: string | null
  decidedByFullName: string | null
  dayHalf: DayHalf
  workloadMismatch?: boolean   // #26: úvazek v termínu ≠ hodiny ve snapshotu → ⚠
}

export enum AbsenceRequestStatus {
  Pending = 1,
  Approved = 2,
  Rejected = 3,
  Cancelled = 4,
}

/** Část dne u půldenní žádosti (0,5 dne, jeden den) — #11. */
export enum DayHalf {
  Full = 0,       // celý den (i vícedenní)
  Morning = 1,    // dopoledne
  Afternoon = 2,  // odpoledne
}

export const DAY_HALF_LABEL: Record<DayHalf, string> = {
  [DayHalf.Full]: '',
  [DayHalf.Morning]: 'dopoledne',
  [DayHalf.Afternoon]: 'odpoledne',
}

export interface AbsenceType {
  id: number
  name: string
  nameEn: string
  code: string
  isPaid: boolean
  requiresApproval: boolean
}

/** Typy nepřítomnosti — fixní data ze seedu (IDs 1–8 dle pořadí vložení). */
export const ABSENCE_TYPES: AbsenceType[] = [
  { id: 1, code: 'VACATION',     name: 'Dovolená',                 nameEn: 'Vacation',           isPaid: true,  requiresApproval: true  },
  { id: 2, code: 'SICK',         name: 'Nemoc',                    nameEn: 'Sick Leave',         isPaid: false, requiresApproval: false },
  { id: 3, code: 'UNPAID',       name: 'Neplacené volno',          nameEn: 'Unpaid Leave',       isPaid: false, requiresApproval: true  },
  { id: 4, code: 'OBSTACLE',     name: 'Překážka v práci',         nameEn: 'Work Obstacle',      isPaid: true,  requiresApproval: true  },
  { id: 5, code: 'COMPENSATORY', name: 'Náhradní volno',           nameEn: 'Compensatory Leave', isPaid: true,  requiresApproval: true  },
  { id: 6, code: 'OCR',          name: 'Ošetřování člena rodiny',  nameEn: 'Family Care',        isPaid: false, requiresApproval: false },
  { id: 7, code: 'PAID_SPECIAL', name: 'Placené volno',            nameEn: 'Paid Special Leave', isPaid: true,  requiresApproval: true  },
  { id: 8, code: 'QUARANTINE',   name: 'Karanténa',                nameEn: 'Quarantine',         isPaid: false, requiresApproval: false },
]

export interface CreateAbsenceRequestData {
  employeeId: number
  absenceTypeId: number
  startDate: string
  endDate: string
  location: string
  note?: string
  totalDays?: number
  dayHalf?: DayHalf
  totalHours?: number   // HR ruční override hodin (#19); jinak dny × úvazek
}

export interface UpdateAbsenceRequestData {
  absenceTypeId: number
  startDate: string
  endDate: string
  location: string
  note?: string
  totalDays?: number
  dayHalf?: DayHalf
  totalHours?: number   // HR ruční override hodin (#19); jinak dny × úvazek
}

export const absenceRequestsService = {
  getAll: (params?: { employeeId?: number; status?: number; year?: number; myTeamOnly?: boolean; showAll?: boolean }) =>
    apiClient.get<AbsenceRequest[]>('/absencerequests', { params }).then(r => r.data),

  create: (data: CreateAbsenceRequestData) =>
    apiClient.post<{ id: number }>('/absencerequests', data).then(r => r.data),

  update: (id: number, data: UpdateAbsenceRequestData) =>
    apiClient.put(`/absencerequests/${id}`, data),

  cancel: (id: number) =>
    apiClient.delete(`/absencerequests/${id}`),

  deletePermanent: (id: number) =>
    apiClient.delete(`/absencerequests/${id}/permanent`),

  decide: (id: number, approved: boolean, managerNote: string | null) =>
    apiClient.post(`/absencerequests/${id}/decide`, { approved, managerNote }),

  /** Vrátí počet pracovních dnů z DB (respektuje svátky a rozvrh zaměstnance). */
  getWorkingDays: (startDate: string, endDate: string, employeeId: number): Promise<number> =>
    apiClient.get<number>('/absencerequests/working-days', {
      params: { startDate, endDate, employeeId }
    }).then(r => r.data),
}
